import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/admin/menu/shared";

/**
 * An item's variations (Half / Full, 30 ml / 60 ml): each has its own price, and
 * billing charges that price instead of the item's. 153 of them were imported and
 * none could be edited from any screen (DESIGN.md §10 V19).
 *
 * Each row saves on its own — the server has one write per variation — and a row
 * says so while it differs from what the server holds.
 */

// Rupees with up to two decimals, and more than zero: the server rejects 0 and below.
// The decimals are checked on the typed text, not as `value * 100` — in floating
// point 19.99 * 100 is 1998.9999999999998, which rejected ₹19.99 and 13% of all
// valid prices between ₹0.01 and ₹10,000.
const priceError = raw => {
  const text = String(raw).trim();
  if (text === "" || !Number.isFinite(Number(text))) return "Enter a price";
  if (Number(text) <= 0) return "Price must be more than ₹0";
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return "At most two decimals";
  return null;
};
export { priceError };

function Row({ itemId, variation, data, readOnly, lastActive = false, basePrice = null }) {
  const [name, setName] = useState(variation.name);
  const [price, setPrice] = useState(String(variation.price));
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { setName(variation.name); setPrice(String(variation.price)); }, [variation.name, variation.price]);

  const dirty = name.trim() !== variation.name || Number(price) !== Number(variation.price);
  const nameErr = touched && !name.trim() ? "Enter a name" : null;
  const priceErr = touched ? priceError(price) : null;

  const save = async () => {
    setTouched(true);
    if (!name.trim() || priceError(price)) return;
    setBusy(true);
    const result = await data.updateVariation(itemId, variation.id, { name: name.trim(), price: Number(price) });
    setBusy(false);
    if (!result.ok) return toast.error(`Could not save "${variation.name}": ${result.error}`);
    toast.success(`"${name.trim()}" saved`);
    setTouched(false);
  };

  const toggle = async active => {
    // Switching off the last size on sale leaves the item at its base price: say so.
    if (!active && lastActive && !window.confirm(
      `"${variation.name}" is the last size on sale. Switched off, the item sells at its base price${basePrice == null ? "" : ` (₹${basePrice})`}. Continue?`
    )) return;
    const result = await data.updateVariation(itemId, variation.id, { is_active: active });
    if (!result.ok) toast.error(`Could not ${active ? "enable" : "disable"} "${variation.name}": ${result.error}`);
  };

  const remove = async () => {
    setConfirmDelete(false);
    const result = await data.deleteVariation(itemId, variation.id);
    if (!result.ok) return toast.error(`Could not delete "${variation.name}": ${result.error}`);
    toast.success(`"${variation.name}" deleted`);
  };

  return (
    <div className="grid grid-cols-[1fr_8rem_auto_auto] items-start gap-2 py-1.5" data-testid={`variation-row-${variation.id}`}>
      <div>
        <Input className="h-8 text-body" value={name} disabled={readOnly} aria-label="Variation name" aria-invalid={Boolean(nameErr)}
          onChange={event => setName(event.target.value)} data-testid={`variation-name-${variation.id}`} />
        {nameErr && <p className="mt-1 text-meta text-danger">{nameErr}</p>}
      </div>
      <div>
        <Input className="h-8 text-body tabular-nums" type="number" min="0.01" step="0.01" value={price} disabled={readOnly}
          aria-label="Variation price" aria-invalid={Boolean(priceErr)}
          onChange={event => setPrice(event.target.value)} data-testid={`variation-price-${variation.id}`} />
        {priceErr && <p className="mt-1 text-meta text-danger" data-testid={`variation-price-error-${variation.id}`}>{priceErr}</p>}
      </div>
      <div className="flex h-8 items-center gap-1.5">
        <Switch checked={variation.is_active} disabled={readOnly} aria-label={`${variation.name} on sale`} onCheckedChange={toggle} data-testid={`variation-active-${variation.id}`} />
      </div>
      <div className="flex h-8 items-center gap-1">
        {!readOnly && dirty && (
          <Button variant="action" type="button" size="sm" className="h-8" onClick={save} disabled={busy} data-testid={`variation-save-${variation.id}`}>
            <Check className="h-4 w-4" />{busy ? "Saving…" : "Save"}
          </Button>
        )}
        {!readOnly && (
          <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-danger"
            onClick={() => setConfirmDelete(true)} aria-label={`Delete ${variation.name}`} data-testid={`variation-delete-${variation.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <ConfirmDialog
        open={confirmDelete}
        title={`Delete "${variation.name}"?`}
        body={lastActive
          ? `This is the item's last size on sale: once it goes, the item sells at its base price (${basePrice == null ? "not set" : `₹${basePrice}`}). Bills already printed keep it. To stop selling it for now, switch it off instead.`
          : "It disappears from billing and captain screens. Bills already printed keep it. To stop selling it for now, switch it off instead."}
        confirmLabel="Delete Variation"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={remove}
        testId={`variation-delete-dialog-${variation.id}`}
      />
    </div>
  );
}

export default function VariationsEditor({ item, data, readOnly }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!item) {
    return <p className="text-meta text-muted-foreground" data-testid="variations-need-item">Save the item first, then add its variations here.</p>;
  }
  const variations = [...(item.variations || [])].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  const nameErr = touched && !name.trim() ? "Enter a name" : null;
  const priceErr = touched ? priceError(price) : null;

  const add = async event => {
    event.preventDefault();
    setTouched(true);
    if (!name.trim() || priceError(price)) return;
    setBusy(true);
    const result = await data.createVariation(item.id, {
      name: name.trim(),
      price: Number(price),
      sort_order: (variations.length + 1) * 10,
    });
    setBusy(false);
    if (!result.ok) return toast.error(`Could not add variation: ${result.error}`);
    toast.success(`"${name.trim()}" added to ${item.name}`);
    setName(""); setPrice(""); setTouched(false);
  };

  return (
    <section className="space-y-1" data-testid="variations-editor">
      <div className="flex items-baseline justify-between">
        <h4 className="text-body font-semibold">Variations</h4>
        <span className="text-meta text-muted-foreground">Each variation is billed at its own price, not the item's.</span>
      </div>
      {variations.length > 0 && (
        <div className="grid grid-cols-[1fr_8rem_auto_auto] gap-2 text-meta font-medium text-muted-foreground">
          <span>Name</span><span>Price (₹)</span><span>On sale</span><span />
        </div>
      )}
      <div className="divide-y">
        {variations.map(variation => (
          <Row
            key={variation.id} itemId={item.id} variation={variation} data={data} readOnly={readOnly}
            lastActive={variation.is_active && variations.filter(v => v.is_active).length === 1}
            basePrice={item.price ?? null}
          />
        ))}
      </div>
      {variations.length === 0 && <p className="text-meta text-muted-foreground">No variations. The item sells at its own price.</p>}
      {!readOnly && (
        <form className="grid grid-cols-[1fr_8rem_auto] items-start gap-2 pt-2" onSubmit={add}>
          <div>
            <Input className="h-8 text-body" value={name} placeholder="e.g. Half" aria-label="New variation name" aria-invalid={Boolean(nameErr)}
              onChange={event => setName(event.target.value)} data-testid="variation-new-name" />
            {nameErr && <p className="mt-1 text-meta text-danger">{nameErr}</p>}
          </div>
          <div>
            <Input className="h-8 text-body tabular-nums" type="number" min="0.01" step="0.01" value={price} placeholder="0.00"
              aria-label="New variation price" aria-invalid={Boolean(priceErr)}
              onChange={event => setPrice(event.target.value)} data-testid="variation-new-price" />
            {priceErr && <p className="mt-1 text-meta text-danger" data-testid="variation-new-price-error">{priceErr}</p>}
          </div>
          <Button type="submit" size="sm" variant="outline" className="h-8" disabled={busy} data-testid="variation-add">
            <Plus className="h-4 w-4" />Add Variation
          </Button>
        </form>
      )}
    </section>
  );
}
